import React, { useCallback, useRef, useState } from 'react';
import { InputActionMeta } from 'react-select';

import Select from './common/Select';
import { getApplicants } from '../api/generated/arrayscapeGamingDashboardAPI';
import type { Applicant } from '../api/generated/model';

// Searchable, server-paginated applicant picker — same pattern as the Sling
// location mapping dropdown (debounced server search + cursor + scroll-to-load).
const DEBOUNCE_MS = 300;

export interface ApplicantOption {
  value: string;
  label: string;
  applicant: Applicant;
}

const labelOf = (a: Applicant): string => {
  const name = [a.first_name, a.last_name].filter(Boolean).join(' ').trim();
  return name ? `${name} — ${a.email}` : (a.email || a.id || '');
};

interface Props {
  value: ApplicantOption | null;
  onChange: (opt: ApplicantOption | null) => void;
  placeholder?: string;
}

const ApplicantSelect: React.FC<Props> = ({ value, onChange, placeholder = 'Search applicants…' }) => {
  const [loaded, setLoaded] = useState<ApplicantOption[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const fetchPage = useCallback(async (search: string, cur: string | null, append: boolean) => {
    try {
      setIsLoading(true);
      const params: any = {};
      if (search) params.search = search;
      if (cur) params.cursor = cur;
      else { params.sort_field = 'name'; params.sort_direction = 'asc'; }
      const res = await getApplicants(params);
      const rows = res.data || [];
      const opts: ApplicantOption[] = rows.map((a) => ({ value: a.id!, label: labelOf(a), applicant: a }));
      setCursor(res.pagination?.cursor || null);
      setHasMore(!!res.pagination?.cursor);
      setLoaded((prev) => (append ? [...prev, ...opts] : opts));
    } catch (e) {
      console.error('applicant search failed', e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const onMenuScrollToBottom = useCallback(async () => {
    if (!hasMore || isLoading || !cursor) return;
    await fetchPage(searchInput, cursor, true);
  }, [hasMore, isLoading, cursor, searchInput, fetchPage]);

  const debouncedSearch = useCallback((input: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchInput(input);
      setCursor(null);
      setHasMore(true);
      fetchPage(input, null, false);
    }, DEBOUNCE_MS);
  }, [fetchPage]);

  const onInputChange = useCallback((v: string, meta: InputActionMeta) => {
    if (meta.action === 'input-change') debouncedSearch(v);
  }, [debouncedSearch]);

  const onMenuOpen = useCallback(() => {
    if (loaded.length === 0) fetchPage('', null, false);
  }, [loaded.length, fetchPage]);

  return (
    <Select<ApplicantOption>
      value={value}
      onChange={(opt) => onChange(opt || null)}
      options={loaded}
      isClearable
      isLoading={isLoading}
      onInputChange={onInputChange}
      onMenuScrollToBottom={onMenuScrollToBottom}
      onMenuOpen={onMenuOpen}
      filterOption={() => true}
      placeholder={placeholder}
      noOptionsMessage={() => (isLoading ? 'Loading…' : 'No applicants found')}
      loadingMessage={() => 'Searching…'}
      menuPortalTarget={document.body}
    />
  );
};

export default ApplicantSelect;
